// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📊 EXERCISE SESSION HISTORY COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getPlayerExerciseSessions, deleteExerciseSession } from '../../services/exerciseSessions';
import type { ExerciseSessionWithDetails, ScoringType } from '../../types';
import './ExerciseSessionHistory.css';

interface ExerciseSessionHistoryProps {
  exerciseId?: number;
}

export const ExerciseSessionHistory: React.FC<ExerciseSessionHistoryProps> = ({
  exerciseId,
}) => {
  const { player } = useAuth();
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<ExerciseSessionWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSessions();
  }, [player, exerciseId]);

  const loadSessions = async () => {
    if (!player) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getPlayerExerciseSessions(player.id);
      // Filter by exerciseId if provided
      const filtered = exerciseId 
        ? data.filter(s => s.exercise_id === exerciseId)
        : data;
      setSessions(filtered);
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setError('Failed to load session history');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sessionId: number) => {
    if (!window.confirm(t('training.sessions.confirmDelete'))) return;

    try {
      await deleteExerciseSession(sessionId);
      loadSessions();
    } catch (err) {
      console.error('Failed to delete session:', err);
      alert(t('training.sessions.deleteFailed'));
    }
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const renderScore = (session: ExerciseSessionWithDetails): string => {
    const scoringType = (session.exercise as any)?.scoring_type as ScoringType;
    const score = session.score_data;
    const scoringConfig = (session.exercise as any)?.scoring_config;

    if (!score) return '-';

    switch (scoringType) {
      case 'points':
        const pointsTarget = scoringConfig?.max_points || scoringConfig?.pass_threshold;
        return pointsTarget 
          ? `${score.total_score || 0} / ${pointsTarget} ${t('training.sessions.points')}`
          : `${score.total_score || 0} ${t('training.sessions.points')}`;
      
      case 'balls_potted':
        const ballsTarget = scoringConfig?.total_balls || scoringConfig?.pass_threshold;
        return ballsTarget
          ? `${score.total_score || 0} / ${ballsTarget} ${t('training.sessions.balls')}`
          : `${score.total_score || 0} ${t('training.sessions.balls')}`;
      
      case 'success_rate':
        const total = (score.success_count || 0) + (score.fail_count || 0);
        if (total === 0) return '-';
        const rate = Math.round(((score.success_count || 0) / total) * 100);
        const rateTarget = scoringConfig?.target_rate || scoringConfig?.pass_threshold;
        return rateTarget
          ? `${rate}% / ${rateTarget}% (${score.success_count}/${total})`
          : `${rate}% (${score.success_count}/${total})`;
      
      case 'time':
        const timeTarget = scoringConfig?.max_time;
        const timeValue = formatDuration(score.best_score || 0);
        return timeTarget
          ? `${timeValue} / ${formatDuration(timeTarget)}`
          : timeValue;
      
      case 'attempts':
        const attemptsTarget = scoringConfig?.max_attempts;
        return attemptsTarget
          ? `${score.best_score || 0} (${t('training.sessions.attempts')}: ${attemptsTarget})`
          : `${score.best_score || 0}`;
      
      default:
        return `${score.total_score || 0}`;
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (sessions.length === 0) {
    return (
      <div className="empty-state">
        <p>{t('training.sessions.noHistory')}</p>
      </div>
    );
  }

  return (
    <div className="session-history">
      <h3>{t('training.sessions.history')}</h3>
      
      <div className="sessions-list">
        {sessions.map(session => (
          <div key={session.id} className="session-card">
            <div className="session-card-header">
              {!exerciseId && (
                <h4>{(session.exercise as any)?.title}</h4>
              )}
              <span className="session-date">{formatDate(session.started_at)}</span>
            </div>

            <div className="session-card-body">
              <div className="session-stat">
                <span className="stat-label">{t('training.sessions.score')}:</span>
                <span className="stat-value">{renderScore(session)}</span>
              </div>

            {(session.duration_minutes !== null && session.duration_minutes !== undefined) && (
              <div className="session-stat">
                <span className="stat-label">{t('training.sessions.duration')}:</span>
                <span className="stat-value">
                  {session.duration_minutes === 0 
                    ? '< 1 min' 
                    : `${session.duration_minutes} min`}
                </span>
              </div>
            )}

              {session.notes && (
                <div className="session-notes-preview">
                  <span className="stat-label">{t('training.sessions.notes')}:</span>
                  <p>{session.notes}</p>
                </div>
              )}
            </div>

            <div className="session-card-actions">
              <button 
                onClick={() => handleDelete(session.id)}
                className="btn-delete-small"
              >
                🗑️ {t('common.delete')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

